import { useContext, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useField } from 'formik';
import type { FieldHookConfig } from 'formik';

import { useComponentDidUpdate } from '../../hooks';
import { FormContext, isRequiredField } from './utils';
import SimpleMaskInput from './SimpleMaskInput';
import type { MaskChangeEvent, SimpleMaskInputProps } from './SimpleMaskInput';

/**
 * Пропсы Formik-обёртки masked-инпута (hooks-стиль: `useField` + `FormContext`,
 * без render-prop — в отличие от StringInput на FastField).
 *
 * `error` из базы НЕ Omit'ится: MaskInput его не деструктурирует — пропс протекает
 * через `{...rest}` в SimpleMaskInput и отображается, т.е. это часть фактического API.
 * `name` фактически обязателен (нужен `useField(props)`), но остаётся optional
 * из базы — не ужесточаем (lenient tsconfig).
 */
export interface MaskInputProps extends Omit<SimpleMaskInputProps, 'required' | 'onComplete'> {
  /** Ручной признак обязательности; иначе автодетект по validationSchema (isRequiredField). */
  required?: boolean;
  onComplete?: (event: MaskChangeEvent) => void;
  /** Колбэк вызывается, когда поле переходит в состояние ошибки валидации. */
  onValidateFail?: (error: unknown) => void;
}

const MaskInput = (props: MaskInputProps) => {
  const [error, setError] = useState(undefined);
  const {
    validationSchema, setFieldValue, setFieldTouched, submitCount,
  } = useContext(FormContext) as {
    validationSchema?: any;
    setFieldValue: any;
    setFieldTouched: any;
    submitCount: number;
  };
  const {
    required,
    onComplete,
    onValidateFail,
    ...rest
  } = props;

  // Приведение: FieldHookConfig конфликтует с наследуемыми HTML-подобными пропсами
  // (напр., maxLength: string | number) — миграция типов, runtime не меняется.
  const [field, meta] = useField(props as FieldHookConfig<any>);

  useComponentDidUpdate(() => {
    if (!isEmpty(meta.error)
      && ((meta.touched && (field.value !== undefined)) || (submitCount > 0))
    ) {
      setError(meta.error);

      if (typeof onValidateFail === 'function') {
        onValidateFail(meta.error);
      }
    } else {
      setError(undefined);
    }
  }, [meta.error, meta.touched, submitCount, field.value]);

  const handleChangeCompleted = (event: MaskChangeEvent) => {
    if (event?.rawValue !== field.value) {
      setFieldValue(props.name, event?.rawValue);
      setFieldTouched(props.name, true, true);
    }

    if (typeof onComplete === 'function') {
      onComplete(event);
    }
  };

  return (
    <SimpleMaskInput
      { ...rest }
      { ...field }
      error={ error }
      required={ required || isRequiredField(validationSchema, field.name) }
      onComplete={ handleChangeCompleted }
    />
  );
};

export default MaskInput;
